import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import SavedPaymentMethodsSelector from '../checkout/components/SavedPaymentMethodsSelector';
import StripeCardForm from '../checkout/components/StripeCardForm';
import {
  useGetSavedPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation
} from '../../redux/apis/endpoints/paymentApi';
import {
  usePurchaseLicenseMutation,
  useConfirmLicenseMutation
} from '../../redux/apis/endpoints/licensingApi';

const STRIPE_KEY = typeof window !== 'undefined' && window._env_
  ? window._env_.REACT_APP_STRIPE_PUBLISHABLE_KEY
  : (typeof process !== 'undefined' && process.env
    ? (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null);

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const BRAND_TEAL = '#14b8a6';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Inner form component that has access to Stripe hooks.
 * IMPORTANT: The CardElement must remain fully visible and mounted during
 * the entire payment flow. Stripe checks element visibility.
 */
const LicensePaymentForm = ({ listing, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState('payment'); // payment | success
  const [error, setError] = useState(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [showCardElement, setShowCardElement] = useState(false);
  const [cardKey] = useState(Date.now());
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: savedPaymentMethods = [] } = useGetSavedPaymentMethodsQuery();
  const [purchaseLicense] = usePurchaseLicenseMutation();
  const [confirmLicense] = useConfirmLicenseMutation();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [savePaymentMethod] = useSavePaymentMethodMutation();

  // Default-select the saved card (so the user can pay in one tap, like checkout)
  useEffect(() => {
    if (savedPaymentMethods.length > 0) {
      setSelectedSavedMethod(savedPaymentMethods.find(m => m.isDefault) || savedPaymentMethods[0]);
      setUseNewCard(false);
    } else {
      setUseNewCard(true);
      setSelectedSavedMethod(null);
    }
  }, [savedPaymentMethods]);

  const handleCardReady = useCallback(() => setShowCardElement(true), []);
  const handleCardChange = useCallback((event) => {
    setError(event.error ? event.error.message : null);
  }, []);

  const handleSubmit = async () => {
    if (!stripe || isProcessing) return;
    setError(null);
    setIsProcessing(true);

    try {
      const purchaseResult = await purchaseLicense({
        listingId: listing.id,
        itemType: listing.itemType || 'listing',
        savedPaymentMethodId: selectedSavedMethod?.id || undefined
      }).unwrap();

      if (!purchaseResult.success) {
        throw new Error(purchaseResult.message || 'Failed to create payment');
      }

      const { clientSecret, paymentIntentId: piId, paymentStatus } = purchaseResult;
      let stripeResult;

      // When using a saved card, the backend auto-confirms the PaymentIntent.
      // Calling confirmCardPayment again would cause Stripe to return a 400.
      if (paymentStatus === 'succeeded') {
        stripeResult = { paymentIntent: { id: piId, status: 'succeeded' } };
      } else if (selectedSavedMethod && !useNewCard) {
        stripeResult = await stripe.confirmCardPayment(clientSecret);
      } else {
        const cardElement = elements.getElement('card');
        if (!cardElement) throw new Error('Card form not loaded. Please refresh and try again.');
        stripeResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement }
        });
      }

      if (stripeResult.error) throw new Error(stripeResult.error.message);

      // Save card if user checked the option
      if (saveCard && useNewCard && stripeResult.paymentIntent?.status === 'succeeded') {
        try {
          const setupResult = await createSetupIntent().unwrap();
          const cardEl = elements.getElement('card');
          if (cardEl) {
            const confirmSetup = await stripe.confirmCardSetup(setupResult.clientSecret, {
              payment_method: { card: cardEl }
            });
            if (!confirmSetup.error) {
              await savePaymentMethod({
                setupIntentId: confirmSetup.setupIntent.id,
                isDefault: savedPaymentMethods.length === 0
              }).unwrap();
            }
          }
        } catch (saveErr) {
          console.warn('Failed to save card:', saveErr);
        }
      }

      const confirmResult = await confirmLicense({
        paymentIntentId: stripeResult.paymentIntent.id
      }).unwrap();

      if (confirmResult.success) {
        setStep('success');
        if (onSuccess) onSuccess(confirmResult.license);
      } else {
        throw new Error('License confirmation failed');
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (step === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#059669' }}>
          <svg viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
            <path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>License Purchased</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
          You now have full access to the clean, unwatermarked image. Download or share it anytime.
        </div>
        <button onClick={onClose} style={{ marginTop: 20, padding: '10px 32px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '6px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Done
        </button>
      </div>
    );
  }

  const price = listing.licensingPrice || 0;
  const currency = listing.currency || 'USD';

  return (
    <>
      {/* Product info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: '#f9fafb', borderRadius: 10, marginBottom: 16 }}>
        {listing.coverImage && (
          <img src={listing.coverImage} alt={listing.name} style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{listing.name}</div>
          {listing.designerName && <div style={{ fontSize: 12, color: '#9ca3af' }}>by {listing.designerName}</div>}
          <div style={{ fontSize: 16, fontWeight: 600, color: BRAND_TEAL, marginTop: 2 }}>
            {currency === 'USD' ? '$' : currency}{price.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {['Clean, unwatermarked try-on image', 'Full-resolution download', 'Share and post anywhere', 'Lifetime access'].map(benefit => (
          <li key={benefit} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151' }}>
            <div style={{ width: 16, height: 16, color: BRAND_TEAL, flexShrink: 0 }}><CheckIcon /></div>
            {benefit}
          </li>
        ))}
      </ul>

      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0 0 14px' }} />

      {/* Payment method */}
      <div style={{ opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
        <SavedPaymentMethodsSelector savedPaymentMethods={savedPaymentMethods} selectedSavedMethod={selectedSavedMethod}
          useNewCard={useNewCard} onSelectedMethodChange={setSelectedSavedMethod} onUseNewCardChange={setUseNewCard} />
        {useNewCard && (
          <>
            <StripeCardForm cardKey={cardKey} showCardElement={showCardElement || true}
              onCardChange={handleCardChange} onCardReady={handleCardReady} saveCard={saveCard} />
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 12 }}>
              <div
                onClick={(e) => { e.preventDefault(); setSaveCard(!saveCard); }}
                style={{
                  width: 16, height: 16, borderRadius: 4, 
                  background: saveCard ? BRAND_TEAL : '#fff',
                  border: `1px solid ${saveCard ? BRAND_TEAL : '#d1d5db'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s'
                }}
              >
                {saveCard && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </div>
              <span style={{ marginLeft: 8, fontSize: 13, color: '#374151' }}>Save this card for future purchases</span>
            </label>
          </>
        )}
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{error}</div>}

      <button onClick={handleSubmit} disabled={isProcessing || !stripe || (useNewCard && !showCardElement)}
        style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '6px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 12, opacity: (isProcessing || !stripe || (useNewCard && !showCardElement)) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {isProcessing && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'licSpin 1s linear infinite', display: 'inline-block' }} />}
        {isProcessing ? 'Processing...' : `Pay ${currency === 'USD' ? '$' : currency}${price.toFixed(2)} for License`}
      </button>
    </>
  );
};

/**
 * LicensePurchaseModal -- renders its own overlay (not using Modal component)
 * to avoid nested-portal z-index issues with the VTO overlay (z-9990).
 */
const LicensePurchaseModal = ({ isOpen, onClose, listing, onSuccess }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || !listing) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes licSpin { to { transform: rotate(360deg); } }
        @keyframes licSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div ref={overlayRef} onClick={handleOverlayClick}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', animation: 'licSlideUp 0.25s ease', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>Purchase Digital License</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1, padding: '4px' }}>&#x2715;</button>
          </div>
          <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <LicensePaymentForm listing={listing} onSuccess={onSuccess} onClose={onClose} />
              </Elements>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: 14 }}>
                Payment system is not configured. Please try again later.
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default LicensePurchaseModal;
